import React, { useState, useEffect } from 'react';
import UserLink from './UserLink';
import {
  User, Mail, Building2, Calendar, Inbox, Wrench, CheckCircle, Timer,
  ArrowLeft, Activity,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const PV = 'rgb(var(--pv-800))';

const PRIORITY_COLORS = {
  urgent: '#dc2626',
  high:   '#ea580c',
  medium: '#2563eb',
  low:    '#64748b',
};

const ROLE_LABELS = {
  admin: 'Admin',
  tech: 'Tech Team',
  'tech-admin': 'Tech Admin',
  facilities: 'Facilities',
  'facilities-admin': 'Facilities Admin',
  custodian: 'Custodian',
  staff: 'Staff',
};

const formatHours = (h) => {
  if (h === null || h === undefined) return '--';
  const n = Number(h);
  if (Number.isNaN(n)) return '--';
  if (n < 1) return `${Math.round(n * 60)}m`;
  if (n < 48) return `${n.toFixed(1)}h`;
  return `${(n / 24).toFixed(1)}d`;
};

const shortDate = (value) => {
  const d = new Date(value);
  if (isNaN(d)) return '--';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const relative = (value) => {
  const d = new Date(value);
  if (isNaN(d)) return '';
  const mins = Math.floor((Date.now() - d) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return shortDate(value);
};

const Stat = ({ label, value, icon: Icon, sub }) => (
  <div className="bg-surface border border-n-200 rounded-xl px-4 py-3">
    <div className="flex items-center gap-1.5 text-n-600">
      {Icon && <Icon className="w-3.5 h-3.5" />}
      <span className="text-xs font-medium">{label}</span>
    </div>
    <div className="mt-1 text-2xl font-semibold text-n-900 tabular-nums">{value}</div>
    {sub && <div className="text-xs text-n-500 mt-0.5">{sub}</div>}
  </div>
);

const TicketList = ({ tickets, emptyText, nameLabel, idLabel, viewer, onOpenTicket, onOpenUser }) => {
  if (!tickets.length) {
    return <div className="text-sm text-n-500 py-8 text-center">{emptyText}</div>;
  }
  return (
    <div className="divide-y divide-n-200">
      {tickets.map((t) => (
        <button
          key={t.id}
          onClick={() => onOpenTicket(t.id)}
          className="w-full text-left px-2 py-3 hover:bg-n-100 transition-colors"
        >
          <div className="flex items-baseline gap-2 min-w-0">
            <span className="text-xs text-n-400 tabular-nums shrink-0">#{t.id}</span>
            <span className="text-sm text-n-900 truncate flex-1">{t.title}</span>
            <span className={`text-xs shrink-0 ${
              t.status === 'resolved' ? 'text-green-700 dark:text-green-400'
                : t.status === 'in-progress' ? 'text-yellow-700 dark:text-yellow-400'
                : 'text-red-700 dark:text-red-400'
            }`}>
              {t.status.replace('-', ' ')}
            </span>
          </div>
          <div className="flex items-center gap-2 mt-1 text-xs text-n-500">
            <span
              className="px-1.5 py-0.5 rounded text-[10px] font-medium"
              style={{
                backgroundColor: `${PRIORITY_COLORS[t.priority] || '#64748b'}22`,
                color: PRIORITY_COLORS[t.priority] || '#64748b',
              }}
            >
              {t.priority}
            </span>
            {t.location && <span className="truncate">{t.location}</span>}
            <span className="ml-auto shrink-0">{shortDate(t.created_at)}</span>
          </div>
          {nameLabel && t[nameLabel] && (
            <div className="text-xs text-n-500 mt-0.5">
              <UserLink
                id={idLabel ? t[idLabel] : null}
                name={t[nameLabel]}
                viewer={viewer}
                onOpen={onOpenUser}
              />
            </div>
          )}
        </button>
      ))}
    </div>
  );
};

const UserDetail = ({ userId, token, viewer, onBack, onOpenTicket, onOpenUser }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('submitted');

  useEffect(() => {
    if (!userId || !token) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/users/${userId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`Could not load this user (${res.status})`);
        const json = await res.json();
        if (!cancelled) {
          setData(json);
          setError('');
          // A teacher's page is about what they've reported; a tech's is about
          // what they're working on. Open on whichever they actually have.
          setTab(json.counts.assigned > 0 && json.counts.submitted === 0 ? 'assigned' : 'submitted');
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [userId, token]);

  if (loading) {
    return <div className="text-center py-16 text-n-500">Loading user...</div>;
  }

  if (error) {
    return (
      <div className="bg-surface border border-n-200 rounded-xl p-8 text-center">
        <p className="text-sm text-n-700">{error}</p>
        <button
          onClick={onBack}
          className="mt-4 px-4 py-2 rounded-lg text-white text-sm font-medium"
          style={{ backgroundColor: PV }}
        >
          Back to users
        </button>
      </div>
    );
  }

  const { user: u, counts, submitted, assigned, activity } = data;
  const isWorker = counts.assigned > 0;

  return (
    <div className="max-w-5xl mx-auto space-y-4">
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 text-n-600 hover:text-n-900 font-medium text-sm"
      >
        <ArrowLeft className="w-4 h-4" /> Back to users
      </button>

      {/* Identity ------------------------------------------------------- */}
      <div className="bg-surface rounded-xl border border-n-200 p-6">
        <div className="flex items-start gap-4">
          <div className="bg-n-200 w-14 h-14 rounded-full flex items-center justify-center shrink-0">
            <User className="w-7 h-7 text-n-600" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-2xl font-bold text-n-900">{u.name}</h2>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-n-600">
              <span className="flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5" />{u.email}
              </span>
              {u.department && (
                <span className="flex items-center gap-1.5">
                  <Building2 className="w-3.5 h-3.5" />
                  {u.department === 'technology' ? 'Technology' : 'Facilities'}
                </span>
              )}
              <span className="flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" />
                Account created {shortDate(u.created_at)}
              </span>
            </div>
          </div>
          <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-n-100 text-n-700 shrink-0">
            {ROLE_LABELS[u.role] || u.role}
          </span>
        </div>
      </div>

      {/* Counts ---------------------------------------------------------- */}
      <div className={`grid grid-cols-2 gap-3 ${isWorker ? 'lg:grid-cols-4' : 'lg:grid-cols-2'}`}>
        <Stat
          label="Tickets submitted" value={counts.submitted} icon={Inbox}
          sub={counts.submitted_open > 0 ? `${counts.submitted_open} still open` : 'none open'}
        />
        <Stat
          label="Last submitted"
          value={counts.last_ticket ? relative(counts.last_ticket) : '--'}
          icon={Calendar}
          sub={counts.first_ticket ? `first was ${shortDate(counts.first_ticket)}` : null}
        />
        {isWorker && (
          <>
            <Stat
              label="Assigned" value={counts.assigned} icon={Wrench}
              sub={counts.assigned_open > 0 ? `${counts.assigned_open} still open` : 'all closed'}
            />
            <Stat
              label="Median time to close"
              value={formatHours(counts.median_resolve_hours)}
              icon={Timer}
              sub={`${counts.resolved_by_them} resolved`}
            />
          </>
        )}
      </div>

      {/* Tickets and activity -------------------------------------------- */}
      <div className="bg-surface rounded-xl border border-n-200">
        <div className="flex border-b border-n-200 px-2">
          {[
            { key: 'submitted', label: `Submitted (${counts.submitted})` },
            ...(isWorker ? [{ key: 'assigned', label: `Assigned (${counts.assigned})` }] : []),
            { key: 'activity', label: 'Activity' },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                tab === t.key ? 'text-n-900' : 'border-transparent text-n-600 hover:text-n-900'
              }`}
              style={tab === t.key ? { borderColor: PV } : undefined}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="p-4">
          {tab === 'submitted' && (
            <TicketList
              tickets={submitted}
              emptyText="This person hasn't submitted any tickets."
              nameLabel="assigned_to_name"
              idLabel="assigned_to"
              viewer={viewer}
              onOpenTicket={onOpenTicket}
              onOpenUser={onOpenUser}
            />
          )}

          {tab === 'assigned' && (
            <TicketList
              tickets={assigned}
              emptyText="Nothing assigned to this person."
              nameLabel="submitted_by_name"
              idLabel="submitted_by"
              viewer={viewer}
              onOpenTicket={onOpenTicket}
              onOpenUser={onOpenUser}
            />
          )}

          {tab === 'activity' && (
            activity.length === 0 ? (
              <div className="text-sm text-n-500 py-8 text-center">
                No recorded activity. The audit trail only covers changes made since
                it was added, so there's nothing here for older work.
              </div>
            ) : (
              <div className="divide-y divide-n-200">
                {activity.map((ev, i) => (
                  <button
                    key={`${ev.ticket_id}-${i}`}
                    onClick={() => onOpenTicket(ev.ticket_id)}
                    className="w-full text-left flex items-start gap-2.5 px-2 py-2.5 hover:bg-n-100 transition-colors"
                  >
                    <Activity className="w-4 h-4 mt-0.5 shrink-0 text-n-500" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-n-700 truncate">
                        {ev.event_type === 'field_changed'
                          ? <>changed {ev.field} to <span className="font-medium text-n-900">{ev.new_value || 'none'}</span></>
                          : ev.event_type === 'assigned'
                            ? <>assigned to <span className="font-medium text-n-900">{ev.new_value || 'nobody'}</span></>
                            : <>{ev.event_type.replace(/_/g, ' ')}</>}
                        {' '}on <span className="text-n-500">#{ev.ticket_id}</span> {ev.title}
                      </p>
                      <p className="text-xs text-n-500">{relative(ev.created_at)}</p>
                    </div>
                  </button>
                ))}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default UserDetail;
